<script setup lang="ts">
import { computed } from "vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VLabel from "@magnit/core/src/components/VLabel/VLabel.vue";
import type { IProductDetailsOfferPriceProps } from "../types/product-details";
import { formatPrice } from "@magnit/unit-catalog/src/utils/converter";
import useProduct from "../composables/useProduct";

const props = defineProps<
  IProductDetailsOfferPriceProps & { ecom?: boolean }
>();

const {
  productPrice,
  productOldPrice,
  productDiscountPercentLabel,
  productWeight,
  productUnitWeight,
} = useProduct(props.product);

const productPriceLabel = computed(() =>
  props.progressivePrice
    ? formatPrice(props.progressivePrice / 100)
    : productPrice.value
      ? formatPrice(productPrice.value)
      : "",
);

const productOldPriceLabel = computed(() =>
  productOldPrice.value ? formatPrice(productOldPrice.value) : "",
);

const discountLabel = computed(
  () => productDiscountPercentLabel.value || props.progressiveDiscountLabel,
);

const priceUnits = computed(() => (props.progressivePrice ? "/шт" : ""));

const isPriceContainerVisible = computed(() =>
  Boolean(productPriceLabel.value || discountLabel.value),
);
</script>

<template>
  <section>
    <div
      v-if="isPriceContainerVisible"
      class="product-details-price__container"
      :class="{ ecom }"
    >
      <VText
        v-if="productPriceLabel"
        class="product-details-price__current"
        :font="{ xs: 'headline-small', l: 'headline-medium' }"
      >
        <span v-html="productPriceLabel" />{{ priceUnits }}
      </VText>
      <div class="product-details-price__old-wrapper">
        <VText
          v-if="productOldPrice && !progressivePrice"
          class="product-details-price__old"
          color="secondary"
          :font="{
            s: 'body-large-regular',
            l: 'title-medium',
          }"
        >
          <span v-html="productOldPriceLabel" />
        </VText>
      </div>
      <VLabel
        v-if="discountLabel"
        class="product-details-price__discount"
        size="s"
        bg-color="var(--u-catalog-product-card-discount-background)"
        text-color="var(--u-catalog-product-card-discount-color)"
      >
        {{ discountLabel }}
      </VLabel>
      <VText
        v-if="productWeight"
        class="product-details-price__weight"
        :font="{ xs: 'title-medium', l: 'headline-medium' }"
        color="secondary"
      >
        {{ productWeight }}
      </VText>
    </div>
    <VText
      v-if="productUnitWeight"
      class="product-details-price__unit"
      :class="{ ecom }"
      tag="div"
      font="caption-large-accent-low"
      color="secondary"
    >
      <span v-html="productUnitWeight" />
    </VText>
  </section>
</template>

<style lang="postcss" scoped>
.product-details-price {
  &__container {
    display: flex;
    align-items: center;

    &.ecom {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .product-details-price__weight,
      .product-details-price__discount {
        display: none !important;
      }
    }
  }

  &__unit.ecom {
    display: none;
  }

  &__current {
    margin-right: var(--pl-unit-x2);

    @media (--pl-viewport-from-l) {
      margin-right: var(--pl-unit-x3);
    }
  }

  &__old-wrapper {
    height: var(--pl-unit-x6);
  }

  &__old {
    margin-right: var(--pl-unit-x2);
    position: relative;
    display: inline-block;
    overflow: hidden;

    &::after {
      /* Спасибо Олегу за эту скруглённую черту :-) */
      content: "";
      position: absolute;
      width: 300%;
      height: 170%;
      border: 1px solid var(--pl-text-accent);
      border-radius: 50%;
      left: -20%;
      top: 18%;
    }
  }

  &__discount {
    display: inline-flex;
  }

  &__weight {
    margin-left: auto;

    @media (--pl-viewport-from-l) {
      color: var(--pl-text-tertiary);
    }
  }
}
</style>
